class LaProductGallery extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;

    // Bound once — survives the innerHTML swaps done in onVariantChange()
    this.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
    this.addEventListener('click', (e) => this.onMobileNavClick(e));

    this.refresh();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
    }
  }

  // Re-query elements and rebind per-content listeners. Called on connect and
  // again after the gallery markup is swapped out for a new variant.
  refresh() {
    this.scrollEl = this.querySelector('.la-gallery__scroll');
    this.thumb = this.querySelector('.la-gallery__indicator-thumb');
    this.track = this.querySelector('.la-gallery__indicator-track');
    this.indicator = this.querySelector('.la-gallery__indicator');

    if (!this.scrollEl) return;

    this.scrollEl.addEventListener('scroll', () => this.onScroll(), { passive: true });

    // Re-check scrollability when the gallery column resizes (e.g. window resize)
    this._ro?.disconnect();
    this._ro = new ResizeObserver(() => this.updateIndicator());
    this._ro.observe(this.scrollEl);

    this.initMobileCarousel();
    this.updateIndicator();
  }

  // ── Variant switching ──────────────────────────────────

  onVariantChange(event) {
    const data = event && event.data;
    if (!data || !data.html || String(data.sectionId) !== String(this.sectionId)) return;

    const newGallery = this.id && data.html.getElementById ? data.html.getElementById(this.id) : null;
    if (!newGallery || newGallery === this) return;

    this.innerHTML = newGallery.innerHTML;
    this.refresh();
  }

  // ── Desktop wheel capture ──────────────────────────────

  handleWheel(e) {
    if (window.innerWidth < 990) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollEl;
    const atTop = scrollTop <= 0 && e.deltaY < 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1 && e.deltaY > 0;

    if (atTop || atBottom) return;

    e.preventDefault();
    this.scrollEl.scrollTop += e.deltaY;
  }

  onScroll() {
    this.updateIndicator();
  }

  // ── Scroll indicator ──────────────────────────────────

  updateIndicator() {
    if (!this.thumb || !this.track) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollEl;
    const isScrollable = window.innerWidth >= 990 && scrollHeight > clientHeight;

    this.indicator?.classList.toggle('is-scrollable', isScrollable);
    if (!isScrollable) return;

    const trackH = this.track.clientHeight;
    const thumbH = this.thumb.offsetHeight;
    const padding = 8;
    const maxTop = trackH - thumbH - padding * 2;
    const ratio = scrollTop / (scrollHeight - clientHeight);
    this.thumb.style.top = `${padding + ratio * maxTop}px`;
  }

  // ── Mobile carousel ───────────────────────────────────

  initMobileCarousel() {
    this.dots = Array.from(this.querySelectorAll('.la-gallery__mobile-dot'));
    this.prevBtn = this.querySelector('.la-gallery__mobile-btn--prev');
    this.nextBtn = this.querySelector('.la-gallery__mobile-btn--next');
    this.counter = this.querySelector('[data-la-gallery-counter]');
    const items = Array.from(this.querySelectorAll('.la-gallery__item'));
    this._mobileIndex = 0;

    const scrollToItem = (index) => {
      const next = Math.max(0, Math.min(items.length - 1, index));
      const target = items[next];
      if (!target || !this.scrollEl) return;
      this.scrollEl.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    };

    this._scrollToMobileItem = scrollToItem;

    this._mobileIo?.disconnect();
    if (!window.IntersectionObserver || !items.length) return;

    this._mobileIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const idx = items.indexOf(entry.target);
            if (idx !== -1) this.setMobileIndex(idx, items.length);
          }
        });
      },
      { root: this.scrollEl, threshold: 0.5 }
    );

    items.forEach((item) => this._mobileIo.observe(item));
    this.setMobileIndex(0, items.length);
  }

  onMobileNavClick(event) {
    if (window.innerWidth >= 990 || !this._scrollToMobileItem) return;

    if (event.target.closest('.la-gallery__mobile-btn--prev')) {
      this._scrollToMobileItem(this._mobileIndex - 1);
      return;
    }

    if (event.target.closest('.la-gallery__mobile-btn--next')) {
      this._scrollToMobileItem(this._mobileIndex + 1);
      return;
    }

    const dot = event.target.closest('.la-gallery__mobile-dot');
    if (dot) {
      const index = this.dots.indexOf(dot);
      if (index !== -1) this._scrollToMobileItem(index);
    }
  }

  setMobileIndex(index, total) {
    this._mobileIndex = index;
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    if (this.counter) {
      const template = this.counter.dataset.template || '{{ current }} / {{ total }}';
      this.counter.textContent = template.replace('__C__', String(index + 1)).replace('__T__', String(total));
    }
    if (this.prevBtn) this.prevBtn.disabled = index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = index >= total - 1;
  }

  updateMobileDots() {
    // Handled by IntersectionObserver in initMobileCarousel
  }

  disconnectedCallback() {
    this._ro?.disconnect();
    this._mobileIo?.disconnect();
    this._variantChangeUnsubscriber?.();
  }
}

if (!customElements.get('la-product-gallery')) {
  customElements.define('la-product-gallery', LaProductGallery);
}
